import { useEffect, useState, useCallback } from "react";

export type UseModalKeyboardNavigationOptions = {
  /** Whether the modal is currently open */
  isOpen: boolean;
  /** List of tab identifiers */
  tabs?: string[];
  /** Initial active tab */
  initialTab?: string;
  /** Whether there's a previous item to navigate to */
  hasPrevious?: boolean;
  /** Whether there's a next item to navigate to */
  hasNext?: boolean;
  /** Callback when navigating to previous item */
  onPrevious?: () => void;
  /** Callback when navigating to next item */
  onNext?: () => void;
};

export type UseModalKeyboardNavigationReturn = {
  /** Current active tab */
  activeTab: string;
  /** Set the active tab */
  setActiveTab: (tab: string) => void;
  /** Go to previous tab */
  goToPreviousTab: () => void;
  /** Go to next tab */
  goToNextTab: () => void;
};

/**
 * Hook for keyboard navigation in modals.
 *
 * - Arrow Up/Down: Navigate between items (previous/next)
 * - Arrow Left/Right: Navigate between tabs (cycles)
 *
 * Navigation is disabled when user is typing in input/textarea fields.
 *
 * @example
 * ```tsx
 * const { activeTab, setActiveTab } = useModalKeyboardNavigation({
 *   isOpen: open,
 *   tabs: ["info", "contracts", "contacts"],
 *   initialTab: "info",
 *   hasPrevious,
 *   hasNext,
 *   onPrevious,
 *   onNext,
 * });
 *
 * return (
 *   <Tabs value={activeTab} onValueChange={setActiveTab}>
 *     ...
 *   </Tabs>
 * );
 * ```
 */
export function useModalKeyboardNavigation({
  isOpen,
  tabs = [],
  initialTab,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
}: UseModalKeyboardNavigationOptions): UseModalKeyboardNavigationReturn {
  // Helper to get a valid tab (clamp to tabs array)
  const getValidTab = useCallback(
    (tab: string | undefined): string => {
      if (tab && tabs.includes(tab)) return tab;
      return tabs[0] ?? "";
    },
    [tabs],
  );

  const [activeTab, setActiveTabState] = useState(() => getValidTab(initialTab));

  // Re-validate activeTab when tabs array changes
  useEffect(() => {
    setActiveTabState((current) => {
      if (tabs.length === 0) return "";
      if (tabs.includes(current)) return current;
      return tabs[0];
    });
  }, [tabs]);

  // Wrapper to ensure we only set valid tabs
  const setActiveTab = useCallback(
    (tab: string) => {
      if (tabs.includes(tab)) {
        setActiveTabState(tab);
      }
    },
    [tabs],
  );

  const goToPreviousTab = useCallback(() => {
    if (tabs.length === 0) return;
    setActiveTabState((current) => {
      const currentIndex = tabs.indexOf(current);
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
      return tabs[prevIndex];
    });
  }, [tabs]);

  const goToNextTab = useCallback(() => {
    if (tabs.length === 0) return;
    setActiveTabState((current) => {
      const currentIndex = tabs.indexOf(current);
      const nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
      return tabs[nextIndex];
    });
  }, [tabs]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;

      // Skip for input/textarea/contentEditable
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }

      // Skip for interactive elements that use arrow keys (selects, comboboxes, menus)
      const role = target.getAttribute("role");
      const interactiveRoles = [
        "combobox",
        "listbox",
        "menu",
        "menubar",
        "menuitem",
        "option",
        "slider",
        "spinbutton",
        "tree",
        "treeitem",
        "grid",
        "gridcell",
      ];
      if (role && interactiveRoles.includes(role)) {
        return;
      }

      // Skip for native select elements
      if (target.tagName === "SELECT") {
        return;
      }

      // Skip if element or ancestor has data-no-keyboard-nav attribute
      if (target.closest("[data-no-keyboard-nav]")) {
        return;
      }

      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          if (hasPrevious) onPrevious?.();
          break;
        case "ArrowDown":
          e.preventDefault();
          if (hasNext) onNext?.();
          break;
        case "ArrowLeft":
          e.preventDefault();
          goToPreviousTab();
          break;
        case "ArrowRight":
          e.preventDefault();
          goToNextTab();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, hasPrevious, hasNext, onPrevious, onNext, goToPreviousTab, goToNextTab]);

  return {
    activeTab,
    setActiveTab,
    goToPreviousTab,
    goToNextTab,
  };
}
